// Canvas skeleton-overlay renderer. Draws a PoseFrame's landmarks + edges onto
// a 2D canvas sized to the video. Pure rendering — reads persisted landmarks so
// it works offline and is reproducible.

import { SKELETON_EDGES, BP, type PoseFrame } from "../pose/landmarks";

export interface OverlayStyle {
  edgeColor: string;
  jointColor: string;
  footColor: string;
  lineWidth: number;
  jointRadius: number;
  minVisibility: number;
}

export const DEFAULT_OVERLAY_STYLE: OverlayStyle = {
  edgeColor: "#7cc4ff",
  jointColor: "#f5f5f7",
  footColor: "#5eead4",
  lineWidth: 3,
  jointRadius: 4,
  minVisibility: 0.4,
};

const FOOT_JOINTS = new Set<number>([
  BP.LEFT_HEEL,
  BP.RIGHT_HEEL,
  BP.LEFT_FOOT_INDEX,
  BP.RIGHT_FOOT_INDEX,
  BP.LEFT_ANKLE,
  BP.RIGHT_ANKLE,
]);

export function drawSkeleton(
  ctx: CanvasRenderingContext2D,
  frame: PoseFrame,
  width: number,
  height: number,
  style: OverlayStyle = DEFAULT_OVERLAY_STYLE,
): void {
  ctx.clearRect(0, 0, width, height);
  const lm = frame.landmarks;

  // Edges
  ctx.lineWidth = style.lineWidth;
  ctx.strokeStyle = style.edgeColor;
  for (const [a, b] of SKELETON_EDGES) {
    const pa = lm[a];
    const pb = lm[b];
    if (!pa || !pb) continue;
    if (pa.visibility < style.minVisibility || pb.visibility < style.minVisibility)
      continue;
    ctx.beginPath();
    ctx.moveTo(pa.x * width, pa.y * height);
    ctx.lineTo(pb.x * width, pb.y * height);
    ctx.stroke();
  }

  // Joints
  for (let i = 0; i < lm.length; i++) {
    const p = lm[i];
    if (!p || p.visibility < style.minVisibility) continue;
    ctx.fillStyle = FOOT_JOINTS.has(i) ? style.footColor : style.jointColor;
    ctx.beginPath();
    ctx.arc(p.x * width, p.y * height, style.jointRadius, 0, Math.PI * 2);
    ctx.fill();
  }
}

export interface TimelineMarker {
  fraction: number; // 0..1 position along the clip
  type: "HS" | "TO";
  side: "L" | "R";
}

/** Build timeline markers (0..1) from events for the scrubber UI. */
export function eventsToMarkers(
  events: { tMs: number; type: "HS" | "TO"; side: "L" | "R" }[],
  durationMs: number,
): TimelineMarker[] {
  if (durationMs <= 0) return [];
  return events.map((e) => ({
    fraction: Math.min(Math.max(e.tMs / durationMs, 0), 1),
    type: e.type,
    side: e.side,
  }));
}

/** Find the frame whose timestamp is closest to a given time. */
export function frameIndexAtMs(frames: PoseFrame[], tMs: number): number {
  if (frames.length === 0) return 0;
  let lo = 0;
  let hi = frames.length - 1;
  while (lo < hi) {
    const mid = (lo + hi) >> 1;
    if (frames[mid].tMs < tMs) lo = mid + 1;
    else hi = mid;
  }
  if (lo > 0 && Math.abs(frames[lo - 1].tMs - tMs) < Math.abs(frames[lo].tMs - tMs))
    return lo - 1;
  return lo;
}
